import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button, Card, CardContent, Dialog, DialogContent, DialogFooter, DialogHeader,
  DialogTitle, Input, PageHeader,
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue,
} from '@erp/ui';
import { Plus, Trash2, Zap } from '@/components/icons';
import {
  eventToShortcut, formatShortcut, normalizeShortcut, useQuickLinks,
} from '@/hooks/use-quick-links';
import { QUICK_LINK_CATALOG, findCatalogEntry } from '@/lib/quick-links-catalog';

export function QuickLinksSettingsPage() {
  const { t } = useTranslation();
  const { links, add, remove, setShortcut, maxLinks } = useQuickLinks();

  const [addOpen, setAddOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string>('');

  const usedKeys = new Set(links.map((l) => l.key));
  const available = QUICK_LINK_CATALOG.filter((e) => !usedKeys.has(e.key));
  const availableNavigate = available.filter((e) => e.group === 'navigate');
  const availableCreate = available.filter((e) => e.group === 'create');
  const atLimit = links.length >= maxLinks;

  const handleAdd = () => {
    if (!selectedKey) return;
    add(selectedKey);
    setSelectedKey('');
    setAddOpen(false);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('settings.quickLinks.title')}
        subtitle={t('settings.quickLinks.subtitle')}
      />

      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <h2 className="text-sm font-medium">{t('settings.quickLinks.listTitle')}</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {t('settings.quickLinks.listHint', { count: links.length, max: maxLinks })}
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => setAddOpen(true)}
              disabled={atLimit || available.length === 0}
            >
              <Plus size={14} />
              {t('settings.quickLinks.add')}
            </Button>
          </div>

          {links.length === 0 ? (
            <div className="rounded-md border border-dashed p-6 text-center">
              <Zap size={20} className="mx-auto text-muted-foreground" />
              <p className="mt-2 text-sm text-muted-foreground">
                {t('settings.quickLinks.empty')}
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-border rounded-md border">
              {links.map((link) => {
                const entry = findCatalogEntry(link.key);
                if (!entry) return null;
                return (
                  <li key={link.key} className="flex items-center gap-3 p-3">
                    <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                      {entry.icon}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{t(entry.labelKey)}</div>
                      <div className="text-xs text-muted-foreground truncate">{entry.path}</div>
                    </div>
                    <ShortcutField
                      value={link.shortcut}
                      onChange={(s) => setShortcut(link.key, s)}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={t('common.delete')}
                      onClick={() => remove(link.key)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}

          <p className="text-xs text-muted-foreground">
            {t('settings.quickLinks.desktopOnlyHint')}
          </p>
        </CardContent>
      </Card>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('settings.quickLinks.addDialogTitle')}</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <Select value={selectedKey} onValueChange={setSelectedKey}>
              <SelectTrigger>
                <SelectValue placeholder={t('settings.quickLinks.selectPage')} />
              </SelectTrigger>
              <SelectContent>
                {availableNavigate.length > 0 && (
                  <SelectGroup>
                    <SelectLabel>{t('settings.quickLinks.groupNavigate')}</SelectLabel>
                    {availableNavigate.map((e) => (
                      <SelectItem key={e.key} value={e.key}>
                        {t(e.labelKey)}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                )}
                {availableCreate.length > 0 && (
                  <SelectGroup>
                    <SelectLabel>{t('settings.quickLinks.groupCreate')}</SelectLabel>
                    {availableCreate.map((e) => (
                      <SelectItem key={e.key} value={e.key}>
                        {t(e.labelKey)}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                )}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleAdd} disabled={!selectedKey}>
              {t('settings.quickLinks.add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ShortcutField({ value, onChange }: { value: string; onChange: (s: string) => void }) {
  const { t } = useTranslation();
  const [recording, setRecording] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!recording) return;
    const handler = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.key === 'Escape') {
        setRecording(false);
        return;
      }
      const captured = eventToShortcut(e);
      if (!captured) return;
      onChange(normalizeShortcut(captured));
      setRecording(false);
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [recording, onChange]);

  return (
    <div className="flex items-center gap-1 shrink-0">
      <Input
        ref={inputRef}
        readOnly
        value={recording ? t('settings.quickLinks.recording') : formatShortcut(value)}
        placeholder={t('settings.quickLinks.shortcutPlaceholder')}
        onClick={() => setRecording(true)}
        className="h-8 w-36 text-xs cursor-pointer text-center font-mono"
      />
      {value && (
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('common.delete')}
          onClick={() => onChange('')}
          className="h-8 w-8"
        >
          <Trash2 size={14} />
        </Button>
      )}
    </div>
  );
}
